import React from 'react';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type {
  WorkoutExerciseRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';
import { WorkoutExercise } from '~/workout/components/workout-exercise/WorkoutExercise';
import { SetsDimensionTable } from '~/workout/components/workout-sets/SetsDimensionTable';
import { useExerciseSetSummary } from '~/workout/hooks/useExerciseSetSummary';

export interface ChatExerciseRowProps {
  exerciseId: string;
  /** Collapsible row (chevron). Defaults to expanded, no chevron. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Interactive exercise row used by the chat workout cards: the builder's
 * `WorkoutExercise` (name, ⋮ menu, editable sets, note) plus the design's
 * set-summary subtitle and optional collapse. Self-resolves its workout context
 * from the exercise record, so it works for standalone-presented exercises.
 */
export const ChatExerciseRow: React.FC<ChatExerciseRowProps> = ({
  exerciseId,
  collapsible,
  defaultExpanded,
  className,
}) => {
  const { t } = useTranslation('workout');

  const {
    data: exerciseRows,
    isLoading: isLoadingExercise,
    error: exerciseError,
  } = useLivePowerSyncQuery(db.Workout.queries.findExerciseById(exerciseId), [
    exerciseId,
  ]);
  const exercise = exerciseRows?.[0] as WorkoutExerciseRecord | undefined;

  const {
    data: activityRows,
    isLoading: isLoadingSportActivity,
    error: sportActivityError,
  } = useLivePowerSyncQuery(
    db.SportActivity.queries.findById(exercise?.sportActivityId ?? null),
    [exercise?.sportActivityId],
  );
  const sportActivity = activityRows?.[0] as SportActivityRecord | undefined;

  const workoutId = exercise?.workoutId ?? '';
  const summary = useExerciseSetSummary(exerciseId, workoutId);

  if (isLoadingExercise || isLoadingSportActivity) {
    return (
      <Text className="text-text-muted text-sm py-3">{t('sets.loading')}</Text>
    );
  }

  if (
    exerciseError ||
    sportActivityError ||
    !exercise ||
    !workoutId ||
    !sportActivity
  ) {
    return null;
  }

  return (
    <WorkoutExercise
      exercise={exercise}
      sportActivity={sportActivity}
      summary={summary}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
      className={className}
    >
      <SetsDimensionTable exerciseId={exerciseId} workoutId={workoutId} />
    </WorkoutExercise>
  );
};
